import { http, type PathParams } from "msw";

import type { ConfirmResetPasswordRequest, LoginRequest } from "@/api/auth";
import type { ChangePasswordRequest } from "@/domains/profile/models/types";
import { findPersonaByEmail, MOCK_PASSWORD } from "@/mocks/personas";
import { withLatency, withSession } from "@/mocks/resolvers";
import { errorResponse, successResponse } from "@/mocks/responses";
import { createMockToken, MOCK_TOKEN_TTL_SECONDS } from "@/mocks/session";

const passwordMismatchResponse = (field: string) => {
  return errorResponse({
    status: 422,
    code: "validation_error",
    message: "The given data was invalid.",
    fields: { [field]: ["The password confirmation does not match."] },
  });
};

export const createAuthHandlers = (webappUrl: string) => {
  return [
    http.post(
      `${webappUrl}/auth/login`,
      withLatency<PathParams, LoginRequest>(async ({ request }) => {
        const { email, password } = await request.json();
        const persona = findPersonaByEmail(email);

        if (!persona || password !== MOCK_PASSWORD) {
          return errorResponse({
            status: 401,
            code: "invalid_credentials",
            message: "These credentials do not match our records.",
          });
        }

        return successResponse({
          accessToken: createMockToken(persona.id),
          tokenType: "bearer",
          expiresIn: MOCK_TOKEN_TTL_SECONDS,
        });
      }),
    ),

    http.post(
      `${webappUrl}/auth/logout`,
      withLatency(() => {
        return successResponse({ message: "Successfully logged out." });
      }),
    ),

    http.post(
      `${webappUrl}/forgot-password`,
      withLatency(() => {
        return successResponse({
          message: "If an account exists for that email, we sent a link to reset the password.",
        });
      }),
    ),

    http.post(
      `${webappUrl}/password/reset/validate`,
      withLatency(() => {
        return successResponse({ valid: true });
      }),
    ),

    http.post(
      `${webappUrl}/reset-password`,
      withLatency<PathParams, ConfirmResetPasswordRequest>(async ({ request }) => {
        const { password, passwordConfirmation } = await request.json();

        if (password !== passwordConfirmation) {
          return passwordMismatchResponse("passwordConfirmation");
        }

        return successResponse({ message: "Your password has been reset." });
      }),
    ),

    http.post(
      `${webappUrl}/password/change`,
      withSession<PathParams, ChangePasswordRequest>(async ({ request }) => {
        const { currentPassword, newPassword, newPasswordConfirmation } = await request.json();

        if (currentPassword !== MOCK_PASSWORD) {
          return errorResponse({
            status: 422,
            code: "validation_error",
            message: "The given data was invalid.",
            fields: { currentPassword: ["The current password is incorrect."] },
          });
        }

        if (newPassword !== newPasswordConfirmation) {
          return passwordMismatchResponse("newPasswordConfirmation");
        }

        return successResponse({ message: "Your password has been changed." });
      }),
    ),
  ];
};
